import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileUpload } from "@/components/FileUpload";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const inputCls = "w-full bg-white border border-[#e7e5e4] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1b5e3f]/20 focus:border-[#1b5e3f] transition-all";

export function ComplaintForm({ open, onClose, onCreated, complaint }) {
  const isEdit = !!complaint;
  const [form, setForm] = useState({ company_name: "", company_email: "", title: "", description: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && complaint) {
      setForm({ company_name: complaint.company_name, company_email: complaint.company_email || "", title: complaint.title, description: complaint.description });
      setFiles((complaint.images || []).map((url) => ({ url, filename: "imagem anexada", category: "photo" })));
    }
    if (open && !complaint) {
      setForm({ company_name: "", company_email: "", title: "", description: "" });
      setFiles([]);
    }
  }, [open, complaint]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, images: files.map((f) => f.url) };
      if (isEdit) {
        await api.put(`/complaints/${complaint.id}`, payload);
        toast.success("Reclamação atualizada!");
      } else {
        await api.post("/complaints", payload);
        toast.success("Reclamação publicada! A empresa foi notificada por e-mail.");
      }
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">{isEdit ? "Editar reclamação" : "Fazer uma reclamação"}</DialogTitle>
          <DialogDescription>{isEdit ? "Atualize os dados da reclamação." : "A reclamação será pública e a empresa será notificada por e-mail."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-[#1c1917]">Nome da empresa *</label>
            <input data-testid="complaint-company-input" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Ex: Loja XYZ" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1c1917]">E-mail da empresa {isEdit ? "" : "*"}</label>
            <input data-testid="complaint-email-input" type="email" required={!isEdit} value={form.company_email} onChange={(e) => setForm({ ...form, company_email: e.target.value })} placeholder={isEdit ? "Deixe em branco para manter o e-mail atual" : "contato@empresa.com"} className={`${inputCls} mt-1.5`} />
            {!isEdit && <p className="text-xs text-[#78716c] mt-1">A empresa receberá a reclamação neste e-mail.</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-[#1c1917]">Título da reclamação *</label>
            <input data-testid="complaint-title-input" required minLength={5} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Resumo do problema" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1c1917]">Descreva o problema *</label>
            <textarea data-testid="complaint-description-input" required minLength={20} rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Conte o que aconteceu, datas, valores e o que você espera da empresa (mín. 20 caracteres)" className={`${inputCls} mt-1.5 resize-y`} />
          </div>
          <FileUpload label="Provas (imagens, opcional)" hint="Prints, fotos, recibos — até 5 imagens" accept="image/*" multiple value={files} onChange={setFiles} testid="complaint-upload" />
          <Button data-testid="complaint-submit-btn" type="submit" disabled={busy} className="w-full rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] py-6 text-base">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : isEdit ? "Salvar alterações" : "Publicar reclamação"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
